import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import {
  Star,
  ArrowRightLeft,
  Banknote,
  Copy,
  Loader2,
  FileText,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { TradeType } from "@/interfaces/trade";
import type { Advertisement } from "@/interfaces/advertisement";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setAmount } from "@/store/slices/p2pMarketSlice";
import { normalizeCurrency } from "@/utils";
import { useNavigate } from "react-router-dom";
import { makeRequest } from "@/utils/makeRequest";
import { useProfile } from "@/hooks";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { toast } from "sonner";
import { formatCurrency } from "@/utils/formatCurrency";
import { queryClient } from "@/services/queryClient";
import { LedgerAccountType } from "@/interfaces/asset";
import { cn } from "@/lib/utils";
import { KycStatus } from "@/interfaces/kyc";

interface Props {
  showTradeModal: boolean;
  setShowTradeModal: Dispatch<SetStateAction<boolean>>;
  side: TradeType;
  crypto: string | null;
  selectedAd: Advertisement | null;
  amount: string;
}

const TradeModal = ({
  showTradeModal,
  setShowTradeModal,
  side,
  crypto,
  selectedAd,
  amount,
}: Props) => {
  const { theme } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  const [isProcessing, setIsProcessing] = useState(false);

  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [signatureName, setSignatureName] = useState("");

  const P2P_TERMS_VERSION = "P2P-TERMS-V1.0";

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState("");

  const userId = user?.id || "";

  const sellerId =
    selectedAd?.type === TradeType.SELL ? selectedAd?.user?.id : null;

  const isSeller = sellerId === userId;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { data: profile } = useProfile(userId);

  const currentAsset = profile?.assets?.find(
    (asset) => asset?.token?.address === selectedAd?.asset?.token?.address,
  );

  const currentAvailableAccount = currentAsset?.accounts?.find(
    (account) => account.type === LedgerAccountType.AVAILABLE,
  );

  const currentAssetBalance = Number(currentAvailableAccount?.balance ?? 0);

  const userAssetFiatPaymentMethods = profile?.paymentMethods?.filter(
    (pm) => pm?.currency?.code === selectedAd?.fiatCurrency?.code,
  );

  const fiatMin = Number(selectedAd?.minAmount) * Number(selectedAd?.price);
  const fiatMax = Number(selectedAd?.maxAmount) * Number(selectedAd?.price);

  const userMaxFiatFromBalance =
    Number(currentAssetBalance) * Number(selectedAd?.price || 0);

  // Final allowed max (what user can actually afford)
  const effectiveFiatMax =
    side === TradeType.SELL
      ? Math.min(fiatMax, userMaxFiatFromBalance)
      : fiatMax;

  const validateTrade = () => {
    if (profile?.kycStatus !== KycStatus.APPROVED) {
      navigate("/dashboard/kyc");
      return false;
    }

    const cryptoAmount = Number(amount) / Number(selectedAd?.price);

    if (!cryptoAmount || cryptoAmount <= 0) {
      toast.error("Please enter a valid amount");
      return false;
    }

    if (side === TradeType.SELL) {
      if (cryptoAmount > Number(currentAssetBalance)) {
        toast.error("Insufficient crypto balance");
        return false;
      }
    }

    if (!selectedAd?.id) {
      toast.error("Advertisement is no longer available");
      return false;
    }

    if (!selectedAd?.price) {
      toast.error("Invalid advertisement price");
      return false;
    }

    if (Number(amount) < fiatMin || Number(amount) > Number(effectiveFiatMax)) {
      toast.error("Amount is outside the allowed trading limits");
      return false;
    }

    if (
      side === TradeType.BUY &&
      userAssetFiatPaymentMethods?.length &&
      !selectedPaymentMethodId
    ) {
      toast.error("Please select a payment method");
      return false;
    }

    return true;
  };

  const handleUseMax = () => {
    if (!selectedAd) return;

    const price = Number(selectedAd.price);

    const adMaxFiat = Number(selectedAd.maxAmount) * price;

    if (side === TradeType.SELL) {
      const walletMaxFiat = Number(currentAssetBalance) * price;

      dispatch(setAmount(Math.min(adMaxFiat, walletMaxFiat).toString()));
    } else {
      dispatch(setAmount(adMaxFiat.toString()));
    }
  };

  const fiatInputAmount = Number(amount || 0);

  const displayCryptoAmount =
    fiatInputAmount && selectedAd?.price
      ? fiatInputAmount / Number(selectedAd.price)
      : 0;

  useEffect(() => {
    if (userAssetFiatPaymentMethods?.length && !selectedPaymentMethodId) {
      setSelectedPaymentMethodId(userAssetFiatPaymentMethods[0].id);
    }
  }, [userAssetFiatPaymentMethods, selectedPaymentMethodId]);

  const formatedPrice = (selectedAd: Advertisement) => {
    if (!selectedAd || !selectedAd.price) {
      return <span>-</span>;
    }

    const min = Number(selectedAd.minAmount);
    const max = Number(selectedAd.maxAmount);
    const price = Number(selectedAd.price);

    return (
      <span>
        {formatCurrency(min * price)} - {formatCurrency(max * price)}
      </span>
    );
  };

  const confirmTrade = async () => {
    if (!termsAccepted) {
      toast.error("You must accept the P2P Terms & Conditions");
      return;
    }

    if (!signatureName.trim()) {
      toast.error("Please provide your electronic signature");
      return;
    }

    if (!profile?.kycStatus || profile.kycStatus !== KycStatus.APPROVED) {
      setShowTermsModal(false);
      navigate("/dashboard/kyc");
      return;
    }

    const cryptoAmount = Number(amount) / Number(selectedAd?.price);

    if (!cryptoAmount || cryptoAmount <= 0) {
      toast.error("Invalid amount");
      return;
    }

    if (side === TradeType.SELL && cryptoAmount > Number(currentAssetBalance)) {
      toast.error("Insufficient crypto balance");
      return;
    }

    if (!selectedAd?.id) {
      toast.error("Advertisement is no longer available");
      return;
    }

    const payload = {
      advertisementId: selectedAd.id,
      amount: cryptoAmount,
      paymentMethodId: selectedPaymentMethodId || undefined,
      side,

      // Terms acceptance
      termsAccepted: true,
      termsVersion: P2P_TERMS_VERSION,
      electronicSignature: signatureName.trim(),
    };

    setIsProcessing(true);

    try {
      const res = await makeRequest.post("/trade", payload);

      if (res) {
        setShowTermsModal(false);

        navigate(`/p2p/${res?.data?.id}`);

        toast.success("Trade initiated successfully!");

        queryClient.invalidateQueries({
          queryKey: ["profile", userId],
        });

        queryClient.invalidateQueries({
          queryKey: ["user", selectedAd?.user?.id],
        });

        queryClient.invalidateQueries({
          queryKey: ["wallet", userId],
        });

        queryClient.invalidateQueries({
          queryKey: ["trades", userId],
        });

        queryClient.invalidateQueries({
          queryKey: ["advertisments:all"],
        });

        dispatch(setAmount(""));
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div>
      <Dialog open={showTradeModal} onOpenChange={setShowTradeModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {side === TradeType.BUY ? (
                <ArrowRightLeft className="w-5 h-5 text-emerald-500" />
              ) : (
                <ArrowRightLeft className="w-5 h-5 text-red-500" />
              )}
              {side === TradeType.BUY ? "Buy" : "Sell"}{" "}
              {normalizeCurrency(crypto ?? "")}{" "}
              {side === TradeType.BUY ? "from" : "to"}{" "}
              {selectedAd?.user?.merchantName}
            </DialogTitle>
            <DialogDescription>
              Complete the trade details below
            </DialogDescription>
          </DialogHeader>
          {selectedAd && (
            <div className="space-y-4 pt-4 -mx-4 sm:mx-0">
              {/* Trader Info */}
              <div
                className={`p-4 rounded-lg ${theme === "dark" ? "bg-muted" : "bg-gray-100"}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F0B90B] to-[#E07B39] flex items-center justify-center text-black font-bold text-lg">
                    {selectedAd?.user?.avatar}
                  </div>
                  <div>
                    <p
                      className={`font-medium ${theme === "dark" ? "text-foreground" : "text-gray-900"}`}
                    >
                      {selectedAd?.user?.merchantName}
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="flex items-center gap-1 text-primary">
                        <Star className="w-3 h-3 fill-[#F0B90B]" />
                        {selectedAd?.user?.reputationScore}
                      </span>
                      <span
                        className={
                          theme === "dark"
                            ? "text-muted-foreground"
                            : "text-gray-500"
                        }
                      >
                        {selectedAd?.trades?.length} trades
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Trade Details */}
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span
                    className={
                      theme === "dark"
                        ? "text-muted-foreground"
                        : "text-gray-500"
                    }
                  >
                    Price
                  </span>
                  <span
                    className={`font-mono font-medium ${theme === "dark" ? "text-foreground" : "text-gray-900"}`}
                  >
                    {Number(selectedAd?.price).toFixed(2)}{" "}
                    {selectedAd.fiatCurrency &&
                      normalizeCurrency(selectedAd.fiatCurrency.code)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span
                    className={
                      theme === "dark"
                        ? "text-muted-foreground"
                        : "text-gray-500"
                    }
                  >
                    Available
                  </span>
                  <span
                    className={
                      theme === "dark" ? "text-foreground" : "text-gray-900"
                    }
                  >
                    {formatCurrency(selectedAd?.available ?? 0.0)}{" "}
                    {selectedAd?.asset?.currency &&
                      normalizeCurrency(selectedAd?.asset?.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span
                    className={
                      theme === "dark"
                        ? "text-muted-foreground"
                        : "text-gray-500"
                    }
                  >
                    Limits
                  </span>
                  <span
                    className={
                      theme === "dark" ? "text-foreground" : "text-gray-900"
                    }
                  >
                    {formatedPrice(selectedAd)}{" "}
                    {normalizeCurrency(selectedAd?.fiatCurrency?.code ?? "USD")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span
                    className={
                      theme === "dark"
                        ? "text-muted-foreground"
                        : "text-gray-500"
                    }
                  >
                    Payment Methods
                  </span>
                  <span
                    className={
                      theme === "dark" ? "text-foreground" : "text-gray-900"
                    }
                  >
                    {selectedAd?.paymentMethods
                      ?.map((pm) => pm?.bank?.name)
                      .join(", ")}
                  </span>
                </div>
              </div>

              {/* Bank Details (for buyers) */}
              {side === TradeType.BUY && selectedAd.paymentMethods && (
                <div
                  className={`p-4 rounded-lg border ${theme === "dark" ? "bg-card border-border" : "bg-white border-gray-200"}`}
                >
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Banknote className="w-4 h-4 text-primary" />
                    Seller's Bank Details
                  </h4>
                  <div className="flex flex-col gap-5">
                    {selectedAd?.paymentMethods.map((pm) => (
                      <div key={pm.id} className="space-y-2 text-sm border-b-2">
                        <div className="flex justify-between">
                          <span
                            className={
                              theme === "dark"
                                ? "text-muted-foreground"
                                : "text-gray-500"
                            }
                          >
                            Bank
                          </span>
                          <span>{pm?.bank?.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span
                            className={
                              theme === "dark"
                                ? "text-muted-foreground"
                                : "text-gray-500"
                            }
                          >
                            Account Name
                          </span>
                          <span>{pm?.accountName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span
                            className={
                              theme === "dark"
                                ? "text-muted-foreground"
                                : "text-gray-500"
                            }
                          >
                            Account Number
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono">
                              {pm?.accountNumber}
                            </span>
                            <button
                            //   onClick={() =>
                            //     copyToClipboard(
                            //       pm?.accountNumber,
                            //     )
                            //   }
                            >
                              <Copy className="w-4 h-4 text-muted-foreground hover:text-primary" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Terms */}
              <div
                className={`p-4 rounded-lg ${theme === "dark" ? "bg-muted" : "bg-gray-100"}`}
              >
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" />
                  Terms & Conditions
                </h4>
                <div className="space-y-2">
                  {selectedAd?.terms?.map((term, i) => (
                    <div
                      key={i}
                      className="border rounded-lg p-3 bg-background/50"
                    >
                      <p className="text-sm font-medium">
                        {term.title || `Term ${i + 1}`}
                      </p>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {term.description}
                      </p>
                    </div>
                  ))}
                </div>
                {/* <p
                className={`text-sm ${theme === "dark" ? "text-muted-foreground" : "text-gray-600"}`}
              >
                {selectedAd?.terms}
              </p> */}
              </div>

              {/* Payment Method Selection */}
              {selectedAd?.type === TradeType.BUY && (
                <>
                  {/* Payment Method Selection */}
                  <div
                    className={`p-4 rounded-lg border ${
                      theme === "dark"
                        ? "bg-card border-border"
                        : "bg-white border-gray-200"
                    }`}
                  >
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Banknote className="w-4 h-4 text-primary" />
                      Select Payment Method
                    </h4>

                    {profile?.paymentMethods?.length ? (
                      <div className="space-y-2">
                        {userAssetFiatPaymentMethods?.map((pm) => (
                          <button
                            key={pm.id}
                            onClick={() => setSelectedPaymentMethodId(pm.id)}
                            className={`w-full text-left p-3 rounded-lg border transition flex items-center justify-between ${
                              selectedPaymentMethodId === pm.id
                                ? "border-primary bg-primary/10"
                                : theme === "dark"
                                  ? "border-border hover:bg-muted"
                                  : "border-gray-200 hover:bg-gray-50"
                            }`}
                          >
                            <div className="space-y-1">
                              <p className="text-sm font-medium">
                                {pm.bank?.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {pm.accountName} • {pm.accountNumber}
                              </p>
                            </div>

                            {selectedPaymentMethodId === pm.id && (
                              <Check className="w-4 h-4 text-primary" />
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div
                        className={`text-sm p-3 rounded-lg ${
                          theme === "dark" ? "bg-muted" : "bg-gray-50"
                        }`}
                      >
                        <p className="mb-3 text-muted-foreground">
                          No payment methods found. You need to add one to
                          continue trading.
                        </p>

                        <Button
                          onClick={() =>
                            navigate("/dashboard/settings?tab=payment-methods")
                          }
                          className={cn(
                            "w-full bg-primary hover:bg-primary/90",
                            selectedAd?.type === TradeType.BUY
                              ? "bg-red-500"
                              : "bg-emerald-500",
                          )}
                        >
                          Add Payment Method
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Amount Input */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span
                    className={`text-xs ${
                      theme === "dark"
                        ? "text-muted-foreground"
                        : "text-gray-500"
                    }`}
                  >
                    Available Balance
                  </span>
                  <span className="text-xs font-medium text-primary">
                    {side === TradeType.SELL ? (
                      <>
                        {formatCurrency(currentAssetBalance)}{" "}
                        {normalizeCurrency(selectedAd?.asset?.currency ?? "")}
                      </>
                    ) : (
                      "No crypto balance required"
                    )}
                  </span>
                </div>
                <label
                  className={`block text-sm mb-2 ${theme === "dark" ? "text-muted-foreground" : "text-gray-500"}`}
                >
                  Amount (
                  {selectedAd?.fiatCurrency &&
                    normalizeCurrency(selectedAd?.fiatCurrency?.code)}
                  )
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => dispatch(setAmount(e.target.value))}
                    placeholder={`${selectedAd?.price ? Number(selectedAd?.minAmount) * Number(selectedAd?.price) : "0"} - ${selectedAd?.price ? Number(selectedAd?.maxAmount) * Number(selectedAd?.price) : ""}`}
                    className={`w-full px-4 py-3 pr-20 rounded-lg border text-sm focus:outline-none focus:border-primary ${
                      theme === "dark"
                        ? "bg-muted border-border text-foreground placeholder:text-muted-foreground"
                        : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"
                    }`}
                  />

                  <button
                    type="button"
                    onClick={handleUseMax}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 transition"
                  >
                    MAX
                  </button>
                </div>
                {amount && (
                  <>
                    <p
                      className={`text-sm mt-2 ${theme === "dark" ? "text-muted-foreground" : "text-gray-500"}`}
                    >
                      You will {side === TradeType.BUY ? "receive" : "sell"}:{" "}
                      <span className="text-primary font-medium">
                        {displayCryptoAmount.toFixed(2)}{" "}
                        {normalizeCurrency(crypto ?? "")}
                      </span>
                    </p>
                    {side === TradeType.SELL &&
                      Number(amount) > userMaxFiatFromBalance && (
                        <p className="text-xs text-red-500 mt-1">
                          Insufficient crypto balance for this amount
                        </p>
                      )}
                  </>
                )}
              </div>

              {/* Confirm Button */}
              <Button
                onClick={() => {
                  if (!validateTrade()) return;

                  setTermsAccepted(false);
                  setSignatureName("");
                  setShowTermsModal(true);
                }}
                disabled={
                  !amount ||
                  Number(amount) < fiatMin ||
                  Number(amount) > Number(effectiveFiatMax) ||
                  isProcessing
                }
                className={`w-full h-14 ${
                  side === TradeType.BUY
                    ? "bg-emerald-500 hover:bg-emerald-600"
                    : "bg-red-500 hover:bg-red-600"
                }`}
              >
                <Check className="w-5 h-5 mr-2" />
                Review Terms & Continue
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showTermsModal} onOpenChange={setShowTermsModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl">
              P2P Trade Terms & Conditions
            </DialogTitle>

            <DialogDescription>
              Please review the terms below before initiating this P2P
              transaction.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-2 space-y-5 text-sm">
            {/* Important Notice */}
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
              <div className="flex gap-3">
                <div className="mt-0.5">
                  <FileText className="w-5 h-5 text-amber-500" />
                </div>

                <div>
                  <p className="font-semibold text-amber-600 dark:text-amber-400">
                    Important Transaction Notice
                  </p>

                  <p className="mt-1 text-muted-foreground leading-6">
                    P2P transactions involve the exchange of digital assets
                    between users. Please verify all transaction details,
                    payment information and instructions before proceeding.
                  </p>
                </div>
              </div>
            </div>

            {/* 1 */}
            <section>
              <h4 className="font-semibold mb-2">1. Trade Information</h4>

              <p className="text-muted-foreground leading-6">
                By initiating this transaction, you confirm that the trade
                information displayed to you, including the asset, amount,
                exchange rate, payment currency, payment method and trading
                counterparty, is correct.
              </p>
            </section>

            {/* 2 */}
            <section>
              <h4 className="font-semibold mb-2">2. Payment Responsibility</h4>

              <p className="text-muted-foreground leading-6">
                For P2P purchases, you agree to make payment only through the
                payment method specified in the trade and according to the
                instructions provided by the counterparty or platform.
              </p>
            </section>

            {/* 3 */}
            <section>
              <h4 className="font-semibold mb-2">3. Transaction Accuracy</h4>

              <p className="text-muted-foreground leading-6">
                You are responsible for ensuring that payment details, account
                information and transaction amounts are accurate before
                submitting or confirming any payment.
              </p>
            </section>

            {/* 4 */}
            <section>
              <h4 className="font-semibold mb-2">
                4. Irreversible Transactions
              </h4>

              <p className="text-muted-foreground leading-6">
                Blockchain asset transfers may be irreversible once confirmed on
                the network. You should carefully verify the transaction details
                before approving any blockchain transaction.
              </p>
            </section>

            {/* 5 */}
            <section>
              <h4 className="font-semibold mb-2">5. Prohibited Transactions</h4>

              <p className="text-muted-foreground leading-6">
                You must not use the P2P marketplace for fraudulent,
                unauthorized, illegal or otherwise prohibited activities. The
                platform may restrict, suspend or investigate transactions where
                suspicious activity or violations of applicable rules are
                identified.
              </p>
            </section>

            {/* 6 */}
            <section>
              <h4 className="font-semibold mb-2">6. Disputes</h4>

              <p className="text-muted-foreground leading-6">
                If a dispute occurs, you agree to provide accurate information
                and supporting evidence requested by the platform. The platform
                may review relevant trade, payment and system records when
                resolving a dispute.
              </p>
            </section>

            {/* 7 */}
            <section>
              <h4 className="font-semibold mb-2">7. Platform Protection</h4>

              <p className="text-muted-foreground leading-6">
                The platform may temporarily restrict withdrawals, transfers,
                trading activity or account functionality where necessary to
                protect users, investigate suspicious activity, comply with
                applicable requirements, or resolve a transaction dispute.
              </p>
            </section>

            {/* 8 */}
            <section>
              <h4 className="font-semibold mb-2">8. Fees</h4>

              <p className="text-muted-foreground leading-6">
                You acknowledge that applicable platform, network, payment
                processing or other disclosed transaction fees may apply. Any
                applicable fee should be reviewed before confirming the
                transaction.
              </p>
            </section>

            {/* 9 */}
            <section>
              <h4 className="font-semibold mb-2">9. Electronic Acceptance</h4>

              <p className="text-muted-foreground leading-6">
                By checking the acceptance box and providing your electronic
                signature below, you confirm that you have reviewed and agree to
                these P2P Terms & Conditions for this transaction.
              </p>
            </section>

            {/* Trade Summary */}
            <div className="rounded-lg border bg-muted/50 p-4">
              <h4 className="font-semibold mb-3">Transaction Summary</h4>

              <div className="space-y-2">
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Transaction</span>

                  <span className="font-medium">
                    {side === TradeType.BUY ? "Buy" : "Sell"}{" "}
                    {normalizeCurrency(crypto ?? "")}
                  </span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Fiat Amount</span>

                  <span className="font-medium">
                    {formatCurrency(Number(amount))}{" "}
                    {normalizeCurrency(selectedAd?.fiatCurrency?.code ?? "USD")}
                  </span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Crypto Amount</span>

                  <span className="font-medium">
                    {displayCryptoAmount.toFixed(2)}{" "}
                    {normalizeCurrency(crypto ?? "")}
                  </span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Counterparty</span>

                  <span className="font-medium">
                    {selectedAd?.user?.merchantName}
                  </span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Terms Version</span>

                  <span className="font-mono text-xs">{P2P_TERMS_VERSION}</span>
                </div>
              </div>
            </div>

            {/* Acceptance */}
            <div className="rounded-lg border p-4 space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-primary"
                />

                <span className="text-sm leading-6">
                  I have read, understood and agree to the P2P Terms &
                  Conditions. I confirm that the transaction details shown above
                  are accurate and that I am authorized to initiate this
                  transaction.
                </span>
              </label>

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Electronic Signature
                </label>

                <input
                  type="text"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  placeholder="Enter your full name"
                  className={`w-full px-4 py-3 rounded-lg border text-sm
              focus:outline-none focus:border-primary
              ${
                theme === "dark"
                  ? "bg-muted border-border text-foreground placeholder:text-muted-foreground"
                  : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"
              }`}
                />

                <p className="text-xs text-muted-foreground">
                  Enter your name as your electronic signature for this
                  transaction.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowTermsModal(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>

            <Button
              onClick={confirmTrade}
              disabled={!termsAccepted || !signatureName.trim() || isProcessing}
              className={`${
                side === TradeType.BUY
                  ? "bg-emerald-500 hover:bg-emerald-600"
                  : "bg-red-500 hover:bg-red-600"
              }`}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Initiating Trade...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Agree & Initiate Trade
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TradeModal;
